import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UploadSimple, FileText, Sparkle, Lightbulb } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line } from "recharts";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { PLATFORMS } from "../lib/meta";
import { Button } from "../components/ui/button";
import { Explainer } from "../components/friendly";

export default function Insights() {
  const { activeClientId, activeClient, refreshOrg } = useApp();
  const [summary, setSummary] = useState(null);
  const [ai, setAi] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const fileRef = useRef();
  const navigate = useNavigate();

  const load = useCallback(() => {
    if (!activeClientId) return;
    api.get(`/insights/summary?client_id=${activeClientId}`).then((r) => setSummary(r.data));
    api.get(`/ai/insights-analysis?client_id=${activeClientId}`).then((r) => setAi(r.data?.headline ? r.data : null));
  }, [activeClientId]);
  useEffect(load, [load]);

  const generateAi = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/insights-analysis", { client_id: activeClientId });
      setAi(data);
      refreshOrg();
      toast.success("AI insights generated (1 credit)");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate insights");
    } finally { setAiBusy(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("client_id", activeClientId);
    fd.append("file", file);
    try {
      const { data } = await api.post("/insights/import", fd);
      toast.success(`Imported ${data.imported} rows`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    }
    e.target.value = "";
  };

  const t = summary?.totals || {};
  const timeline = (summary?.rows || []).reduce((acc, r) => {
    if (!r.date) return acc;
    const d = String(r.date).slice(0, 10);
    const e = acc.find((x) => x.date === d);
    if (e) e.impressions += r.impressions || 0; else acc.push({ date: d, impressions: r.impressions || 0 });
    return acc;
  }, []).sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div className="space-y-6 fade-up" data-testid="insights-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-3xl tracking-tight">Insights</h1>
          <p className="text-sm text-slate-500 mt-1">Performance for <b>{activeClient?.name}</b> — import CSV/XLSX exports from any platform.</p>
        </div>
        <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={upload} data-testid="insights-file-input" />
        <div className="flex gap-2">
          <Button data-testid="open-report-button" variant="outline" onClick={() => navigate("/reports")} className="rounded-lg gap-2">
            <FileText size={15} /> White-label report
          </Button>
          <Button data-testid="insights-import-button" onClick={() => fileRef.current?.click()} className="rounded-lg gap-2 bg-slate-900 hover:bg-slate-700">
            <UploadSimple size={15} /> Import CSV / XLSX
          </Button>
        </div>
      </div>
      <Explainer id="insights">Download the analytics export (CSV or Excel) from any platform and drop it here. Celebre maps the columns automatically, builds the charts, and can write plain-English advice on what to post more of.</Explainer>

      <div className="bg-slate-950 text-white rounded-2xl p-6 sm:p-8 card-float" data-testid="ai-insights-section">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <Sparkle size={20} weight="fill" className="text-[hsl(9,69%,70%)]" />
            <h2 className="font-display font-bold text-lg tracking-tight">AI Strategy Insights</h2>
            {ai?.generated_at && <span className="text-[11px] text-slate-400">updated {new Date(ai.generated_at).toLocaleDateString()}</span>}
          </div>
          <Button data-testid="generate-ai-insights-button" onClick={generateAi} disabled={aiBusy}
            className="rounded-lg gap-2 bg-[hsl(9,69%,62%)] hover:bg-[hsl(8,54%,54%)]">
            <Sparkle size={14} weight="fill" /> {aiBusy ? "Analyzing…" : ai ? "Refresh (1 credit)" : "Generate insights (1 credit)"}
          </Button>
        </div>
        {ai ? (
          <div className="mt-6 space-y-6">
            <p data-testid="ai-insights-headline" className="font-display font-bold text-xl sm:text-2xl tracking-tight leading-snug text-[hsl(9,69%,80%)]">"{ai.headline}"</p>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 font-medium mb-3">What the data says</p>
                <ul className="space-y-2.5">
                  {(ai.findings || []).map((f, i) => (
                    <li key={i} className="flex gap-2.5 text-[13px] text-slate-300 leading-relaxed"><span className="text-[hsl(9,69%,70%)] font-bold shrink-0">→</span>{f}</li>
                  ))}
                </ul>
                {ai.content_mix_advice && (
                  <p className="mt-4 text-[12px] text-slate-400 border-l-2 border-slate-700 pl-3 leading-relaxed">{ai.content_mix_advice}</p>
                )}
              </div>
              <div className="space-y-3">
                <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 font-medium">Do this next</p>
                {(ai.recommendations || []).map((r, i) => (
                  <div key={i} data-testid={`ai-recommendation-${i}`} className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <p className="text-[13px] font-semibold flex items-center gap-2"><Lightbulb size={14} weight="duotone" className="text-amber-300" /> {r.title}</p>
                    <p className="mt-1.5 text-[12px] text-slate-400 leading-relaxed">{r.detail}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <p className="mt-4 text-[13px] text-slate-400">Turn imported numbers into a strategy — specific, data-backed recommendations for <b className="text-slate-200">{activeClient?.name}</b>, grounded in their Brand Intelligence.</p>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {[["Posts", t.posts], ["Impressions", t.impressions], ["Likes", t.likes], ["Comments", t.comments], ["Shares", t.shares]].map(([label, v]) => (
          <div key={label} className="bg-white border border-slate-200/80 rounded-xl p-4 card-float">
            <p className="text-[11px] uppercase tracking-[0.15em] text-slate-400 font-medium">{label}</p>
            <p className="font-display font-bold text-2xl mt-1" data-testid={`insight-total-${label.toLowerCase()}`}>{(v || 0).toLocaleString()}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200/80 rounded-xl p-5 card-float">
          <h2 className="font-display font-bold text-base mb-4">Impressions by platform</h2>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={summary?.by_platform || []}>
              <XAxis dataKey="platform" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} width={50} /><Tooltip />
              <Bar dataKey="impressions" fill="hsl(9,69%,62%)" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-slate-200/80 rounded-xl p-5 card-float">
          <h2 className="font-display font-bold text-base mb-4">Impressions over time</h2>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={timeline}>
              <XAxis dataKey="date" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 11 }} width={50} /><Tooltip />
              <Line type="monotone" dataKey="impressions" stroke="hsl(168,46%,34%)" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white border border-slate-200/80 rounded-xl card-float overflow-x-auto">
        <div className="px-5 py-4 border-b border-slate-100"><h2 className="font-display font-bold text-base">Top posts</h2></div>
        <table className="w-full text-[13px]">
          <thead><tr className="text-left text-[11px] uppercase tracking-widest text-slate-400">
            <th className="px-5 py-2 font-medium">Post</th><th className="px-3 py-2 font-medium">Platform</th><th className="px-3 py-2 font-medium">Impressions</th><th className="px-3 py-2 font-medium">Likes</th>
          </tr></thead>
          <tbody>
            {(summary?.top_posts || []).map((r) => (
              <tr key={r.row_id} className="border-t border-slate-100">
                <td className="px-5 py-2.5 max-w-xs truncate text-slate-700">{r.post || "—"}</td>
                <td className="px-3 py-2.5 text-slate-500">{PLATFORMS[r.platform]?.label || r.platform}</td>
                <td className="px-3 py-2.5 font-semibold">{(r.impressions || 0).toLocaleString()}</td>
                <td className="px-3 py-2.5 text-slate-500">{(r.likes || 0).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!summary?.top_posts?.length && <p className="px-5 py-8 text-sm text-slate-400">No performance data yet — import a file to get started.</p>}
      </div>
    </div>
  );
}
